// frontend\src\components\ManageBatches.js
import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import {
  Autocomplete,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  Alert,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import PlaylistAddCheckCircleRoundedIcon from '@mui/icons-material/PlaylistAddCheckCircleRounded';
import QrCodeScannerRoundedIcon from '@mui/icons-material/QrCodeScannerRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import ViewListRoundedIcon from '@mui/icons-material/ViewListRounded';
import Layout from './Layout';

const clients = [
  { id: 34, name: 'Au Vodka', shortName: 'AU' },
  { id: 33, name: 'Branston', shortName: 'BS' },
  { id: 40, name: 'Black Mirror', shortName: 'BM' },
  { id: 20, name: 'Chapel Down', shortName: 'CD' },
  { id: 13, name: 'Domaine Watson', shortName: 'DW' },
  { id: 11, name: 'Echo Three', shortName: 'E3' },
  { id: 35, name: 'Fox & Vamp', shortName: 'FV' },
  { id: 36, name: 'Funky Pigeon', shortName: 'FP' },
  { id: 8, name: 'Glenmorangie', shortName: 'GL' },
  //{ id: 22, name: 'Glenfiddich', shortName: 'GF' },
  { id: 29, name: 'Food Hugggers UK', shortName: 'FH' },
  { id: 27, name: 'Lady A', shortName: 'LA' },
  { id: 43, name: 'Maree Wines', shortName: 'MW' },
  //{ id: 28, name: 'NOOH by La Coste', shortName: 'NOOH' },
  { id: 14, name: 'Renais', shortName: 'RS' },
  { id: 10, name: 'Schwartz', shortName: 'SWZ' },
  //{ id: 21, name: 'Who Gives a Crap', shortName: 'WGAC' },
  { id: 37, name: 'Wobbly Life', shortName: 'WL' },
  //{ id: 38, name: 'Wolfies', shortName: 'WF' },
  { id: 44, name: 'Krude Oil', shortName: 'KO' },
  { id: 45, name: 'The Naked Doula', shortName: 'TND' },
  { id: 48, name: 'No 98 Beauty', shortName: 'NO98' },
  { id: 49, name: 'Edmunds Cocktails', shortName: 'EC' },
].sort((a, b) => a.name.localeCompare(b.name));

const MOONPIG_CLIENT_ID = clients.find(c => c.name === 'Moonpig')?.id;
const CHAPEL_DOWN_CLIENT_ID = clients.find(c => c.name === 'Chapel Down')?.id;
const DEFAULT_BATCH_SIZE = 35;
const MIN_BATCH_SIZE = 1;
const MAX_BATCH_SIZE = 100;
const BATCH_SIZE_STORAGE_KEY = 'manageBatches.batchSizeByClient';
const PICK_TYPE_STORAGE_KEY = 'manageBatches.pickType';
const SORT_ORDER_OLDEST_FIRST = 'OLDEST_FIRST';
const SORT_ORDER_NEWEST_FIRST = 'NEWEST_FIRST';
const DEFAULT_BATCH_SIZE_BY_CLIENT = {
  [CHAPEL_DOWN_CLIENT_ID]: 10,
};
const MAX_VISIBLE_TAG_CHIPS = 2;
const DEFAULT_PICK_TYPE = 'Bulk';
const VALID_PICK_TYPES = new Set(['Standard', 'Bulk', 'MultiTote']);
const PREVIEW_DENSITY_COMFORTABLE = 'comfortable';
const PREVIEW_DENSITY_DENSE = 'dense';
const FIELD_LABEL_SX = {
  mb: 0.75,
  fontSize: '0.73rem',
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: 'text.secondary',
};
const PANEL_SX = {
  p: { xs: 1.25, md: 1.5 },
  borderRadius: 3,
  borderColor: (theme) => alpha(theme.palette.primary.main, 0.12),
  background: (theme) => `linear-gradient(180deg, ${alpha(theme.palette.primary.main, 0.035)} 0%, ${alpha(theme.palette.background.paper, 1)} 28%)`,
};
const TABLE_HEAD_CELL_SX = {
  fontWeight: 700,
  fontSize: '0.75rem',
  letterSpacing: '0.05em',
  textTransform: 'uppercase',
  color: 'text.secondary',
  backgroundColor: 'background.paper',
};
const MOBILE_PREVIEW_CARD_SX = {
  p: 1.25,
  borderRadius: 2.5,
  borderColor: (theme) => alpha(theme.palette.primary.main, 0.12),
  background: (theme) => `linear-gradient(180deg, ${alpha(theme.palette.primary.main, 0.04)} 0%, ${alpha(theme.palette.background.paper, 1)} 78%)`,
  boxShadow: '0 12px 28px rgba(15, 23, 42, 0.04)',
};
const ORDER_DETAIL_LABEL_SX = {
  fontSize: '0.68rem',
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: 'text.secondary',
};

const normalizeTagValue = (value) =>
  Array.isArray(value)
    ? value
    : typeof value === 'string'
      ? value.split(',').map((tag) => tag.trim()).filter(Boolean)
      : [];

const dedupeTagNames = (tags) => {
  const seen = new Set();
  return tags.filter((tag) => {
    const key = normalizeTagKey(tag);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const canonicalizeTagSelection = (tags, tagNames, { allowUnknown = false } = {}) =>
  dedupeTagNames(
    normalizeTagValue(tags)
      .map((tag) => tag.trim())
      .filter(Boolean)
      .map((tag) => {
        const knownTag = tagNames.find(
          (candidate) => normalizeTagKey(candidate) === normalizeTagKey(tag)
        );
        return knownTag || (allowUnknown ? tag : null);
      })
      .filter(Boolean)
  );

const replaceTagInSelection = (tags, currentName, nextName) =>
  dedupeTagNames(
    normalizeTagValue(tags).map((tag) => (
      normalizeTagKey(tag) === normalizeTagKey(currentName) ? nextName : tag
    ))
  );

const removeTagFromSelection = (tags, tagName) =>
  dedupeTagNames(
    normalizeTagValue(tags).filter(
      (tag) => normalizeTagKey(tag) !== normalizeTagKey(tagName)
    )
  );

const sanitizeBatchSize = (value, fallback = DEFAULT_BATCH_SIZE) => {
  const parsed = Number.parseInt(String(value), 10);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.min(Math.max(parsed, MIN_BATCH_SIZE), MAX_BATCH_SIZE);
};

const loadStoredBatchSizes = () => {
  if (typeof window === 'undefined') return {};

  try {
    const stored = JSON.parse(window.localStorage.getItem(BATCH_SIZE_STORAGE_KEY) || '{}');
    if (!stored || typeof stored !== 'object' || Array.isArray(stored)) return {};

    return Object.entries(stored).reduce((acc, [clientKey, value]) => {
      acc[clientKey] = sanitizeBatchSize(
        value,
        DEFAULT_BATCH_SIZE_BY_CLIENT[clientKey] || DEFAULT_BATCH_SIZE
      );
      return acc;
    }, {});
  } catch (error) {
    console.warn('Failed to load stored batch size preferences:', error);
    return {};
  }
};

const normalizePickType = (value) => (
  VALID_PICK_TYPES.has(value) ? value : DEFAULT_PICK_TYPE
);

const normalizeTagKey = (tag) =>
  String(tag || '')
    .replace(/\s+/g, '')
    .toLowerCase();

const isCardOnlyTag = (tag) =>
  normalizeTagKey(tag) === 'cardonly';

const isNoCardTag = (tag) =>
  normalizeTagKey(tag) === 'nocard';

const loadStoredPickType = () => {
  if (typeof window === 'undefined') return DEFAULT_PICK_TYPE;

  try {
    return normalizePickType(window.localStorage.getItem(PICK_TYPE_STORAGE_KEY));
  } catch (error) {
    console.warn('Failed to load stored pick type preference:', error);
    return DEFAULT_PICK_TYPE;
  }
};


const ManageBatches = () => {
  const [clientId, setClientId] = useState('');
  const [includeTags, setIncludeTags] = useState([]);
  const [excludeTags, setExcludeTags] = useState([]);
  const [managedTags, setManagedTags] = useState([]);
  const [tagsLoading, setTagsLoading] = useState(false);
  const [orders, setOrders] = useState([]);
  const [totalOrdersFetched, setTotalOrdersFetched] = useState(0);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  // NEW: State for pick type
  const [pickType, setPickType] = useState(loadStoredPickType);
  const [orderListText, setOrderListText] = useState('');
  const [mode, setMode] = useState('load');
  const [batchSizeByClient, setBatchSizeByClient] = useState(loadStoredBatchSizes);
  const [batchSortOrder, setBatchSortOrder] = useState(SORT_ORDER_OLDEST_FIRST);
  const [previewDensity, setPreviewDensity] = useState(PREVIEW_DENSITY_COMFORTABLE);
  const [manageTagsOpen, setManageTagsOpen] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [editingTagId, setEditingTagId] = useState(null);
  const [editingTagValue, setEditingTagValue] = useState('');
  const [tagToDelete, setTagToDelete] = useState(null);
  const [tagActionLoading, setTagActionLoading] = useState(false);
  const handleSnackbarClose = () =>
    setSnackbar((prev) => ({ ...prev, open: false }));
  const [printDispatch, setPrintDispatch] = useState(true);

  const prevClientId = useRef();
  const availableTags = managedTags.map((tag) => tag.name);
  const resolvedBatchSize = clientId
    ? batchSizeByClient[String(clientId)]
      || DEFAULT_BATCH_SIZE_BY_CLIENT[String(clientId)]
      || DEFAULT_BATCH_SIZE
    : DEFAULT_BATCH_SIZE;
  const selectedClient = clients.find((client) => String(client.id) === String(clientId));
  const parsedOrderNumbers = orderListText
    .split(/\r?\n/)
    .map((entry) => entry.trim())
    .filter(Boolean);
  const isDensePreview = previewDensity === PREVIEW_DENSITY_DENSE;

  const serializeTags = (tags) =>
    normalizeTagValue(tags)
      .map((tag) => tag.trim())
      .filter(Boolean)
      .join(',');

  const normalizeTagSelection = (tags) =>
    canonicalizeTagSelection(tags, availableTags, { allowUnknown: true });

  const renderTagChips = (value, getTagProps) => {
    const selectedTags = normalizeTagValue(value);
    const visibleTags = selectedTags.slice(0, MAX_VISIBLE_TAG_CHIPS);
    const hiddenCount = selectedTags.length - visibleTags.length;

    return [
      ...visibleTags.map((tag, index) => {
        const tagProps = getTagProps({ index });
        return (
          <Chip
            key={`${tag}-${index}`}
            label={tag}
            size="small"
            {...tagProps}
            sx={{
              maxWidth: 110,
              '& .MuiChip-label': {
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              },
            }}
          />
        );
      }),
      ...(hiddenCount > 0 ? [<Chip key="hidden-tags-count" label={`+${hiddenCount}`} size="small" />] : []),
    ];
  };

  const renderOrderTagChips = (tags) => {
    const normalizedTags = normalizeTagValue(tags);
    if (!normalizedTags.length) {
      return <Typography variant="body2" color="text.secondary">-</Typography>;
    }

    const visibleTags = normalizedTags.slice(0, MAX_VISIBLE_TAG_CHIPS);
    const hiddenCount = normalizedTags.length - visibleTags.length;
    const chips = (
      <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
        {visibleTags.map((tag, index) => (
          <Chip
            key={`${tag}-${index}`}
            label={tag}
            size="small"
            variant="outlined"
            sx={{
              maxWidth: 140,
              borderColor: (theme) => alpha(theme.palette.primary.main, 0.16),
              backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.04),
              '& .MuiChip-label': {
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              },
            }}
          />
        ))}
        {hiddenCount > 0 ? <Chip label={`+${hiddenCount}`} size="small" variant="outlined" /> : null}
      </Stack>
    );

    if (hiddenCount <= 0) {
      return chips;
    }

    return (
      <Tooltip
        arrow
        placement="top-start"
        enterDelay={120}
        title={(
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.35, py: 0.25 }}>
            {normalizedTags.map((tag, index) => (
              <Typography key={`${tag}-tooltip-${index}`} variant="caption" sx={{ color: 'inherit', lineHeight: 1.3 }}>
                {tag}
              </Typography>
            ))}
          </Box>
        )}
      >
        <Box component="span" sx={{ display: 'inline-flex', maxWidth: '100%' }}>
          {chips}
        </Box>
      </Tooltip>
    );
  };

  const fetchTagOptions = useCallback(async ({ setDefaultExclude = false } = {}) => {
    setTagsLoading(true);
    try {
      const res = await axios.get('/api/manage-batches/tags');
      if (res.data?.success) {
        const tags = (res.data.tags || [])
          .filter((row) => row?.name)
          .map((row) => ({
            id: row.id,
            name: String(row.name).trim(),
          }));
        const tagNames = tags.map((tag) => tag.name);

        setManagedTags(tags);
        setIncludeTags((prev) =>
          canonicalizeTagSelection(prev, tagNames)
        );
        setExcludeTags((prev) => {
          const normalizedPrev = normalizeTagValue(prev);
          if (normalizedPrev.length) {
            return canonicalizeTagSelection(normalizedPrev, tagNames);
          }

          if (setDefaultExclude) {
            const defaultCardOnlyTag = tagNames.find(isCardOnlyTag);
            return defaultCardOnlyTag ? [defaultCardOnlyTag] : [];
          }

          return [];
        });
      } else {
        setSnackbar({
          open: true,
          message: res.data?.message || 'Could not load tag options.',
          severity: 'warning',
        });
      }
    } catch (err) {
      console.error(err);
      setSnackbar({
        open: true,
        message: `Failed to load tags: ${err.message}`,
        severity: 'error',
      });
    } finally {
      setTagsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTagOptions({ setDefaultExclude: true });
  }, [fetchTagOptions]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(BATCH_SIZE_STORAGE_KEY, JSON.stringify(batchSizeByClient));
  }, [batchSizeByClient]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    window.localStorage.setItem(PICK_TYPE_STORAGE_KEY, normalizePickType(pickType));
  }, [pickType]);

  useEffect(() => {
    if (clientId === MOONPIG_CLIENT_ID) {
      const moonpigIncludeTag = availableTags.find(isNoCardTag) || 'No Card';
      setIncludeTags((prev) => {
        if (prev.length === 1 && prev[0] === moonpigIncludeTag) return prev;
        return [moonpigIncludeTag];
      });
    } else if (prevClientId.current === MOONPIG_CLIENT_ID) {
      setIncludeTags((prev) =>
        prev.length === 1 && isNoCardTag(prev[0]) ? [] : prev
      );
    }
    prevClientId.current = clientId;
  }, [clientId, availableTags]);

  // Converts Base64 to Blob for printing
  function b64toBlob(b64Data, contentType) {
    const byteCharacters = atob(b64Data);
    const byteArrays = [];
    const sliceSize = 512;

    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
      const slice = byteCharacters.slice(offset, offset + sliceSize);
      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }
      byteArrays.push(new Uint8Array(byteNumbers));
    }
    return new Blob(byteArrays, { type: contentType });
  }

  /**
   * Fetch the latest orders from the server. 
   * 
   * @param {boolean} silent - If true, skip showing spinner to avoid flickering on quick fetch before create batch.
   * @returns {object} The response data containing { success, orders, totalOrders } 
   */
  const fetchOrders = async (silent = false) => {
    if (!clientId) {
      setSnackbar({
        open: true,
        message: 'Please select a client.',
        severity: 'warning',
      });
      setTotalOrdersFetched(0);
      return { success: false, orders: [], totalOrders: 0 };
    }
    if (!silent) setLoading(true);
    setOrders([]);
    setTotalOrdersFetched(0);
    try {
      const params = { clientId, sortOrder: batchSortOrder };
      const includeTagValue = serializeTags(includeTags);
      const excludeTagValue = serializeTags(excludeTags);
      if (includeTagValue) params.includeTags = includeTagValue;
      if (excludeTagValue) params.excludeTags = excludeTagValue;

      const res = await axios.get('/api/manage-batches/orders', { params });

      if (res.data.success) {
        setOrders(res.data.orders);
        setTotalOrdersFetched(Number.parseInt(res.data.totalOrders, 10) || 0);
        if (res.data.orders.length === 0) {
          setSnackbar({
            open: true,
            message: 'No orders matched your criteria.',
            severity: 'info',
          });
        }
      } else {
        setSnackbar({
          open: true,
          message: res.data.message || 'No orders returned.',
          severity: 'info',
        });
      }
      return res.data;
    } catch (err) {
      console.error(err);
      setSnackbar({
        open: true,
        message: `Fetch failed: ${err.message}`,
        severity: 'error',
      });
      setTotalOrdersFetched(0);
      return { success: false, orders: [], totalOrders: 0 };
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const handleBatchSizeChange = (event) => {
    if (!clientId) return;

    const nextBatchSize = sanitizeBatchSize(event.target.value, resolvedBatchSize);
    setBatchSizeByClient((prev) => ({
      ...prev,
      [String(clientId)]: nextBatchSize,
    }));
  };

  const closeManageTagsDialog = () => {
    if (tagActionLoading) return;
    setManageTagsOpen(false);
    setNewTagName('');
    setEditingTagId(null);
    setEditingTagValue('');
    setTagToDelete(null);
  };

  const handleAddTag = async () => {
    const nextTagName = newTagName.trim().replace(/\s+/g, ' ');

    if (!nextTagName) {
      setSnackbar({
        open: true,
        message: 'Enter a tag name before adding it.',
        severity: 'warning',
      });
      return;
    }

    setTagActionLoading(true);
    try {
      const res = await axios.post('/api/manage-batches/tags', { name: nextTagName });
      if (res.data?.success) {
        setNewTagName('');
        await fetchTagOptions();
        setSnackbar({
          open: true,
          message: `Tag "${res.data.tag?.name || nextTagName}" added.`,
          severity: 'success',
        });
      } else {
        setSnackbar({
          open: true,
          message: res.data?.message || 'Failed to add tag.',
          severity: 'error',
        });
      }
    } catch (err) {
      console.error(err);
      setSnackbar({
        open: true,
        message: err.response?.data?.message || `Failed to add tag: ${err.message}`,
        severity: 'error',
      });
    } finally {
      setTagActionLoading(false);
    }
  };

  const beginEditingTag = (tag) => {
    setEditingTagId(tag.id);
    setEditingTagValue(tag.name);
  };

  const cancelEditingTag = () => {
    if (tagActionLoading) return;
    setEditingTagId(null);
    setEditingTagValue('');
  };

  const handleSaveTagEdit = async () => {
    const nextTagName = editingTagValue.trim().replace(/\s+/g, ' ');
    const currentTag = managedTags.find((tag) => String(tag.id) === String(editingTagId));

    if (!currentTag) {
      setSnackbar({
        open: true,
        message: 'Could not find the tag to update.',
        severity: 'error',
      });
      return;
    }

    if (!nextTagName) {
      setSnackbar({
        open: true,
        message: 'Tag name is required.',
        severity: 'warning',
      });
      return;
    }

    setTagActionLoading(true);
    try {
      const res = await axios.put(`/api/manage-batches/tags/${currentTag.id}`, { name: nextTagName });
      if (res.data?.success) {
        const previousName = res.data.previousName || currentTag.name;
        const updatedName = res.data.tag?.name || nextTagName;

        setIncludeTags((prev) => replaceTagInSelection(prev, previousName, updatedName));
        setExcludeTags((prev) => replaceTagInSelection(prev, previousName, updatedName));
        await fetchTagOptions();
        setEditingTagId(null);
        setEditingTagValue('');
        setSnackbar({
          open: true,
          message: `Tag renamed to "${updatedName}".`,
          severity: 'success',
        });
      } else {
        setSnackbar({
          open: true,
          message: res.data?.message || 'Failed to update tag.',
          severity: 'error',
        });
      }
    } catch (err) {
      console.error(err);
      setSnackbar({
        open: true,
        message: err.response?.data?.message || `Failed to update tag: ${err.message}`,
        severity: 'error',
      });
    } finally {
      setTagActionLoading(false);
    }
  };

  const handleDeleteTag = async () => {
    if (!tagToDelete) return;

    setTagActionLoading(true);
    try {
      const res = await axios.delete(`/api/manage-batches/tags/${tagToDelete.id}`);
      if (res.data?.success) {
        const deletedName = res.data.deletedTag?.name || tagToDelete.name;

        setIncludeTags((prev) => removeTagFromSelection(prev, deletedName));
        setExcludeTags((prev) => removeTagFromSelection(prev, deletedName));
        await fetchTagOptions();
        setTagToDelete(null);
        setEditingTagId((prev) => (
          String(prev) === String(res.data.deletedTag?.id) ? null : prev
        ));
        setEditingTagValue('');
        setSnackbar({
          open: true,
          message: `Tag "${deletedName}" removed.`,
          severity: 'success',
        });
      } else {
        setSnackbar({
          open: true,
          message: res.data?.message || 'Failed to delete tag.',
          severity: 'error',
        });
      }
    } catch (err) {
      console.error(err);
      setSnackbar({
        open: true,
        message: err.response?.data?.message || `Failed to delete tag: ${err.message}`,
        severity: 'error',
      });
    } finally {
      setTagActionLoading(false);
    }
  };

  /**
   * Re-fetch orders right before creating a batch to ensure the list is fresh
   * and no one else has taken those orders in the meantime.
   */
  const createBatch = async () => {
    setLoading(true);
    try {
      // 1) Re-fetch orders in "silent" mode for freshness
      const freshData = await fetchOrders(true);
      const freshOrders = freshData.orders || [];
      if (freshOrders.length === 0) {
        setSnackbar({
          open: true,
          message: 'No orders to create a batch from after refresh.',
          severity: 'warning',
        });
        setLoading(false);
        return;
      }
      // 2) Use first page orders
      const firstPageOrders = freshOrders.slice(0, resolvedBatchSize);
      const orderIds = firstPageOrders.map((order) => order.ID);
      const selectedClientObj = clients.find((c) => c.id === clientId);
      const shortName = selectedClientObj?.shortName || 'XX';
      // 3) Pass pickType along with the other values
      const res = await axios.post('/api/manage-batches/create-batch', {
        orderIds,
        clientId,
        includeTags,
        shortName,
        batchSize: resolvedBatchSize,
        pickType,  // NEW: Include pick type selection
      });
      if (res.data.success && res.data.batch.Success) {
        setSnackbar({
          open: true,
          message: `Batch ${res.data.batch.ID} created: ${res.data.batch.Message}`,
          severity: 'success',
        });
        if (res.data.mergedDespatchNote) {
          const pdfBlob = b64toBlob(res.data.mergedDespatchNote, 'application/pdf');
          const pdfUrl = URL.createObjectURL(pdfBlob);
          const printWindow = window.open(pdfUrl);
          if (printWindow) {
            printWindow.onload = () => {
              printWindow.print();
            };
          }
        }
        setOrders([]);
        setTotalOrdersFetched(0);
      } else {
        setSnackbar({
          open: true,
          message: `Batch failed: ${res.data.batch?.Message || 'Unknown error'}`,
          severity: 'error',
        });
      }
    } catch (err) {
      console.error(err);
      setSnackbar({
        open: true,
        message: `Batch creation failed: ${err.response?.data?.message || err.message || 'Unknown error'}`,
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  /**
 * Create batch from a newline-delimited list of order numbers
 */
  const createBatchFromList = async () => {
    if (!clientId) {
      setSnackbar({ open: true, message: 'Select client first', severity: 'warning' });
      return;
    }
    const orderNumbers = orderListText
      .split('\n')
      .map(l => l.trim())
      .filter(l => l !== '');
    if (orderNumbers.length === 0) {
      setSnackbar({ open: true, message: 'No orders entered', severity: 'warning' });
      return;
    }
    setLoading(true);
    try {
      const shortName = clients.find(c => c.id === clientId)?.shortName || 'XX';
      const res = await axios.post('/api/manage-batches/create-batch-by-numbers', {
        orderNumbers,
        clientId,
        includeTags,
        shortName,
        pickType
      });
      if (res.data.success && res.data.batch.Success) {
        setSnackbar({ open: true, message: `Batch ${res.data.batch.ID} created`, severity: 'success' });
        if (printDispatch) {
          // old: print merged PDF
          const blob = b64toBlob(res.data.mergedDespatchNote, 'application/pdf');
          const url = URL.createObjectURL(blob);
          const w = window.open(url);
          if (w) w.onload = () => w.print();
        } else {
          // NEW: include Client Name & Pick Type
          const { ID } = res.data.batch;
          const ref = res.data.reference;
          const clientName = clients.find(c => c.id === clientId)?.name || '—';
          const html = `
         <!doctype html>
         <html>
           <head>
             <meta charset="utf-8">
             <title>Batch ${ID}</title>
             <style>
               @page { size: A4 portrait; margin: 2cm; }
               html, body { margin: 0; padding: 0; }
               body {
                 font-family: sans-serif;
                 font-size: 24px;
                 display: flex;
                 flex-direction: column;
                 justify-content: center;
                 align-items: center;
               }
               .field { margin: 0.5em 0; }
               .label { font-weight: normal; }
               .value { font-weight: bold; }
             </style>
           </head>
           <body>
             <div class="field">
               <span class="label">Client:</span>
               <span class="value">${clientName}</span>
             </div>
             <div class="field">
               <span class="label">Pick Type:</span>
               <span class="value">${pickType}</span>
             </div>
             <div class="field">
               <span class="label">Batch ID:</span>
               <span class="value">${ID}</span>
             </div>
             <div class="field">
               <span class="label">Reference:</span>
               <span class="value">${ref}</span>
             </div>
           </body>
         </html>`;

          const printWin = window.open('', '_blank');
          printWin.document.write(html);
          printWin.document.close();
          printWin.focus();
          printWin.print();
          printWin.close();
        }
        setOrderListText('');
      } else {
        setSnackbar({ open: true, message: `Batch failed: ${res.data.batch?.Message || 'Unknown error'}`, severity: 'error' });
      }
    } catch (err) {
      console.error(err);
      setSnackbar({ open: true, message: `Error: ${err.message}`, severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Manage table pagination
  const displayedOrders = orders.slice(0, resolvedBatchSize);
  const effectiveFetchedOrderTotal = totalOrdersFetched || orders.length;

  return (
    <Layout>
      <Paper
        variant="outlined"
        sx={{
          p: { xs: 1.25, md: 1.5 },
          m: { xs: 1, md: 1.5 },
          display: 'flex',
          flexDirection: 'column',
          minHeight: { md: 'calc(100vh - 120px)' },
          height: { xs: 'auto', md: 'calc(100vh - 120px)' },
          borderRadius: 3,
          overflow: { xs: 'visible', md: 'hidden' },
          borderColor: 'divider',
          background: (theme) => `linear-gradient(180deg, ${alpha(theme.palette.primary.main, 0.045)} 0%, ${alpha(theme.palette.background.paper, 1)} 22%)`,
          boxShadow: '0 18px 40px rgba(15, 23, 42, 0.05)',
        }}
      >
        <Stack spacing={1.25} sx={{ minHeight: 0, flex: 1 }}>
          <Box sx={{ pb: 1.2, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Stack spacing={1.1}>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={1}
                justifyContent="space-between"
                alignItems={{ md: 'flex-start' }}
              >
                <Stack direction="row" spacing={1} alignItems="flex-start">
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 2,
                      display: 'grid',
                      placeItems: 'center',
                      color: 'primary.main',
                      border: '1px solid',
                      borderColor: (theme) => alpha(theme.palette.primary.main, 0.14),
                      backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.07),
                    }}
                  >
                    <Inventory2OutlinedIcon />
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      variant="overline"
                      sx={{ color: 'primary.main', letterSpacing: '0.08em', fontWeight: 700, lineHeight: 1.2 }}
                    >
                      Batch Operations
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
                      Manage Batches
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.2, display: 'block', maxWidth: 760 }}>
                      Create and manage picking batches from Mintsoft orders. Load a queue preview or switch to scan mode for pasted order numbers.
                    </Typography>
                  </Box>
                </Stack>

                <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                  <Chip
                    size="small"
                    color="primary"
                    variant="outlined"
                    label={mode === 'load' ? 'Default workflow' : 'Scan workflow'}
                  />
                  {selectedClient ? (
                    <Chip size="small" variant="outlined" label={selectedClient.name} />
                  ) : null}
                  {mode === 'load' ? (
                    <Chip size="small" variant="outlined" label={`${resolvedBatchSize} per batch`} />
                  ) : null}
                </Stack>
              </Stack>

              <Paper
                variant="outlined"
                sx={{
                  p: 0.3,
                  borderRadius: 2,
                  borderColor: (theme) => alpha(theme.palette.primary.main, 0.14),
                  backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.025),
                  width: { xs: '100%', sm: 'fit-content' },
                }}
              >
                <ToggleButtonGroup
                  exclusive
                  value={mode}
                  onChange={(_event, nextValue) => {
                    if (nextValue) setMode(nextValue);
                  }}
                  sx={{
                    width: { xs: '100%', sm: 'auto' },
                    '& .MuiToggleButton-root': {
                      flex: 1,
                      textTransform: 'none',
                      border: 0,
                      px: 1,
                      py: 0.65,
                      justifyContent: 'flex-start',
                      '&.Mui-selected': {
                        backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.1),
                      },
                    },
                  }}
                >
                  <ToggleButton value="load">
                    <Stack direction="row" spacing={1} alignItems="center">
                      <ViewListRoundedIcon fontSize="small" />
                      <Box sx={{ textAlign: 'left' }}>
                        <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
                          Default Mode
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.15 }}>
                          Load and preview orders
                        </Typography>
                      </Box>
                    </Stack>
                  </ToggleButton>
                  <ToggleButton value="scan">
                    <Stack direction="row" spacing={1} alignItems="center">
                      <QrCodeScannerRoundedIcon fontSize="small" />
                      <Box sx={{ textAlign: 'left' }}>
                        <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
                          Scan Mode
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.15 }}>
                          Paste or scan order numbers
                        </Typography>
                      </Box>
                    </Stack>
                  </ToggleButton>
                </ToggleButtonGroup>
              </Paper>
            </Stack>
          </Box>

          {mode === 'load' ? (
            <Stack spacing={1.25} sx={{ minHeight: 0, flex: 1 }}>
              <Paper variant="outlined" sx={PANEL_SX}>
                <Stack spacing={1.1}>
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={0.75}
                    justifyContent="space-between"
                    alignItems={{ md: 'center' }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="overline" sx={{ color: 'primary.main', fontWeight: 700, letterSpacing: '0.08em', lineHeight: 1.2 }}>
                        Load Queue
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        Select the client and filters for the batch preview.
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.15 }}>
                        Tags, pick type, sort order, and batch size apply before batch creation.
                      </Typography>
                    </Box>
                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      spacing={0.75}
                      alignItems={{ xs: 'stretch', sm: 'center' }}
                      sx={{ alignSelf: { xs: 'stretch', md: 'auto' } }}
                    >
                      <Button
                        variant="outlined"
                        onClick={() => setManageTagsOpen(true)}
                        disabled={tagActionLoading}
                        sx={{ textTransform: 'none', fontWeight: 700, py: 0.7 }}
                      >
                        Manage Tags
                      </Button>
                      <Button
                        variant="contained"
                        onClick={() => fetchOrders(false)}
                        disabled={loading}
                        startIcon={loading ? null : <TuneRoundedIcon />}
                        sx={{ minWidth: 148, textTransform: 'none', fontWeight: 700, py: 0.7 }}
                      >
                        {loading ? <CircularProgress size={22} /> : 'Load Orders'}
                      </Button>
                    </Stack>
                  </Stack>

                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: '1fr',
                        md: 'repeat(2, minmax(0, 1fr))',
                        xl: '1.1fr 1.45fr 1.45fr 0.85fr 0.8fr 0.72fr',
                      },
                      gap: 1.1,
                      alignItems: 'start',
                    }}
                  >
                    <Box>
                      <Typography sx={FIELD_LABEL_SX}>Select Client</Typography>
                      <Select
                        value={clientId}
                        onChange={(e) => setClientId(e.target.value)}
                        displayEmpty
                        fullWidth
                        size="small"
                      >
                        <MenuItem value="" disabled>
                          Choose a client
                        </MenuItem>
                        {clients.map((client) => (
                          <MenuItem key={client.id} value={client.id}>
                            {client.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </Box>

                    <Box>
                      <Typography sx={FIELD_LABEL_SX}>Include Tags</Typography>
                      <Autocomplete
                        multiple
                        disableCloseOnSelect
                        options={availableTags}
                        value={includeTags}
                        loading={tagsLoading}
                        onChange={(_event, newValue) => setIncludeTags(normalizeTagSelection(newValue))}
                        getOptionLabel={(option) => option}
                        renderTags={renderTagChips}
                        disabled={clientId === MOONPIG_CLIENT_ID || tagsLoading}
                        noOptionsText="No tags available. Use Manage Tags to add one."
                        sx={{
                          '& .MuiAutocomplete-inputRoot': {
                            flexWrap: 'nowrap',
                          },
                        }}
                        renderOption={(props, option, { selected }) => (
                          <li {...props}>
                            <Checkbox size="small" checked={selected} />
                            <ListItemText primary={option} />
                          </li>
                        )}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            size="small"
                            placeholder={includeTags.length ? '' : 'All tags'}
                          />
                        )}
                      />
                    </Box>

                    <Box>
                      <Typography sx={FIELD_LABEL_SX}>Exclude Tags</Typography>
                      <Autocomplete
                        multiple
                        disableCloseOnSelect
                        options={availableTags}
                        value={excludeTags}
                        loading={tagsLoading}
                        onChange={(_event, newValue) => setExcludeTags(normalizeTagSelection(newValue))}
                        getOptionLabel={(option) => option}
                        renderTags={renderTagChips}
                        disabled={tagsLoading}
                        noOptionsText="No tags available. Use Manage Tags to add one."
                        sx={{
                          '& .MuiAutocomplete-inputRoot': {
                            flexWrap: 'nowrap',
                          },
                        }}
                        renderOption={(props, option, { selected }) => (
                          <li {...props}>
                            <Checkbox size="small" checked={selected} />
                            <ListItemText primary={option} />
                          </li>
                        )}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            size="small"
                            placeholder={excludeTags.length ? '' : 'No excluded tags'}
                          />
                        )}
                      />
                    </Box>

                    <Box>
                      <Typography sx={FIELD_LABEL_SX}>Pick Type</Typography>
                      <Select
                        value={pickType}
                        onChange={(e) => setPickType(e.target.value)}
                        fullWidth
                        size="small"
                      >
                        <MenuItem value="Standard">Standard</MenuItem>
                        <MenuItem value="Bulk">Bulk</MenuItem>
                        <MenuItem value="MultiTote">MultiTote</MenuItem>
                      </Select>
                    </Box>

                    <Box>
                      <Typography sx={FIELD_LABEL_SX}>Batch Order</Typography>
                      <Select
                        value={batchSortOrder}
                        onChange={(e) => setBatchSortOrder(e.target.value)}
                        fullWidth
                        size="small"
                      >
                        <MenuItem value={SORT_ORDER_OLDEST_FIRST}>Oldest First</MenuItem>
                        <MenuItem value={SORT_ORDER_NEWEST_FIRST}>Newest First</MenuItem>
                      </Select>
                    </Box>

                    <Box>
                      <Typography sx={FIELD_LABEL_SX}>Batch Size</Typography>
                      <TextField
                        type="number"
                        size="small"
                        fullWidth
                        value={resolvedBatchSize}
                        onChange={handleBatchSizeChange}
                        disabled={!clientId}
                        inputProps={{
                          min: MIN_BATCH_SIZE,
                          max: MAX_BATCH_SIZE,
                          step: 1,
                        }}
                        helperText={clientId ? 'Saved per client' : 'Select client first'}
                      />
                    </Box>
                  </Box>
                </Stack>
              </Paper>

              <Paper
                variant="outlined"
                sx={{
                  flex: 1,
                  minHeight: 0,
                  borderRadius: 3,
                  borderColor: (theme) => alpha(theme.palette.primary.main, 0.12),
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                  background: (theme) => `linear-gradient(180deg, ${alpha(theme.palette.primary.main, 0.02)} 0%, ${alpha(theme.palette.background.paper, 1)} 16%)`,
                  boxShadow: '0 16px 36px rgba(15, 23, 42, 0.04)',
                }}
              >
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  spacing={1}
                  justifyContent="space-between"
                  alignItems={{ md: 'center' }}
                  sx={{ px: { xs: 1.25, md: 1.75 }, py: { xs: 1.25, md: 1.5 } }}
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={1.25}
                    alignItems={{ md: 'flex-start' }}
                    sx={{ minWidth: 0, flex: 1 }}
                  >
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography sx={{ ...FIELD_LABEL_SX, mb: 0.35 }}>Batch Preview</Typography>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        Orders To Be Processed
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Review the queue preview before creating a batch. The table below reflects the currently applied client, tag, sort, and batch-size settings.
                      </Typography>
                    </Box>
                    <Paper
                      variant="outlined"
                      sx={{
                        px: 1.25,
                        py: 0.95,
                        minWidth: { xs: '100%', sm: 164 },
                        borderRadius: 2.5,
                        borderColor: (theme) => alpha(theme.palette.primary.main, 0.16),
                        background: (theme) => `linear-gradient(180deg, ${alpha(theme.palette.primary.main, 0.07)} 0%, ${alpha(theme.palette.primary.main, 0.015)} 100%)`,
                      }}
                    >
                      <Typography sx={{ ...FIELD_LABEL_SX, mb: 0.2 }}>
                        Total Fetched
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1 }}>
                        {effectiveFetchedOrderTotal}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.35 }}>
                        Orders waiting for batching
                      </Typography>
                    </Paper>
                  </Stack>
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={0.85}
                    useFlexGap
                    flexWrap="wrap"
                    alignItems={{ xs: 'stretch', sm: 'center' }}
                  >
                    <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                      <Chip size="small" color="primary" variant="outlined" label={`${displayedOrders.length} shown`} />
                      <Chip
                        size="small"
                        variant="outlined"
                        label={batchSortOrder === SORT_ORDER_OLDEST_FIRST ? 'Oldest first' : 'Newest first'}
                      />
                      <Chip size="small" variant="outlined" label={pickType} />
                    </Stack>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 0.35,
                        borderRadius: 2,
                        borderColor: (theme) => alpha(theme.palette.primary.main, 0.12),
                        backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.03),
                        alignSelf: { xs: 'flex-start', sm: 'center' },
                      }}
                    >
                      <ToggleButtonGroup
                        exclusive
                        size="small"
                        value={previewDensity}
                        onChange={(_event, nextValue) => {
                          if (nextValue) setPreviewDensity(nextValue);
                        }}
                        sx={{
                          '& .MuiToggleButton-root': {
                            px: 1.15,
                            py: 0.45,
                            border: 0,
                            textTransform: 'none',
                            fontWeight: 700,
                            fontSize: '0.76rem',
                            '&.Mui-selected': {
                              backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.11),
                            },
                          },
                        }}
                      >
                        <ToggleButton value={PREVIEW_DENSITY_COMFORTABLE}>Comfortable</ToggleButton>
                        <ToggleButton value={PREVIEW_DENSITY_DENSE}>Dense</ToggleButton>
                      </ToggleButtonGroup>
                    </Paper>
                  </Stack>
                </Stack>

                <Divider />

                <Box
                  sx={{
                    display: { xs: 'flex', sm: 'none' },
                    flexDirection: 'column',
                    gap: isDensePreview ? 1 : 1.25,
                    p: 1.25,
                    overflowY: 'auto',
                    flexGrow: 1,
                  }}
                >
                  {displayedOrders.map((order) => (
                    <Paper
                      key={order.ID}
                      variant="outlined"
                      sx={{
                        ...MOBILE_PREVIEW_CARD_SX,
                        p: isDensePreview ? 1 : 1.25,
                      }}
                    >
                      <Stack spacing={isDensePreview ? 0.9 : 1.1}>
                        <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="flex-start">
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                              {order.OrderNumber}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Order ID {order.ID}
                            </Typography>
                          </Box>
                          <Chip
                            size="small"
                            color="primary"
                            variant="outlined"
                            label={`${order.TotalItems} item${Number(order.TotalItems) === 1 ? '' : 's'}`}
                            sx={{ flexShrink: 0 }}
                          />
                        </Stack>

                        <Box
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                            gap: isDensePreview ? 0.9 : 1.1,
                          }}
                        >
                          <Box sx={{ minWidth: 0 }}>
                            <Typography sx={ORDER_DETAIL_LABEL_SX}>External Ref</Typography>
                            <Typography variant="body2" sx={{ mt: 0.25 }} noWrap>
                              {order.ExternalOrderReference || '-'}
                            </Typography>
                          </Box>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography sx={ORDER_DETAIL_LABEL_SX}>Courier</Typography>
                            <Typography variant="body2" sx={{ mt: 0.25 }} noWrap>
                              {order.CourierServiceName || '-'}
                            </Typography>
                          </Box>
                        </Box>

                        <Box>
                          <Typography sx={ORDER_DETAIL_LABEL_SX}>Tags</Typography>
                          <Box sx={{ mt: 0.5 }}>
                            {renderOrderTagChips(order.Tags)}
                          </Box>
                        </Box>
                      </Stack>
                    </Paper>
                  ))}

                  {displayedOrders.length === 0 ? (
                    <Paper variant="outlined" sx={MOBILE_PREVIEW_CARD_SX}>
                      <Stack spacing={0.5} alignItems="center" sx={{ py: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                          No orders to display
                        </Typography>
                        <Typography variant="body2" color="text.secondary" align="center">
                          Load a client queue to preview orders here before creating a batch.
                        </Typography>
                      </Stack>
                    </Paper>
                  ) : null}
                </Box>

                <TableContainer sx={{ flexGrow: 1, overflowY: 'auto', display: { xs: 'none', sm: 'block' } }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={TABLE_HEAD_CELL_SX}>Order ID</TableCell>
                        <TableCell sx={TABLE_HEAD_CELL_SX}>Order Number</TableCell>
                        <TableCell sx={TABLE_HEAD_CELL_SX}>External Ref</TableCell>
                        <TableCell sx={TABLE_HEAD_CELL_SX}>Tags</TableCell>
                        <TableCell sx={TABLE_HEAD_CELL_SX}>Total Items</TableCell>
                        <TableCell sx={TABLE_HEAD_CELL_SX}>Courier Service</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {displayedOrders.map((order) => (
                        <TableRow
                          key={order.ID}
                          hover
                          sx={{
                            cursor: 'default',
                            '& td': {
                              py: isDensePreview ? 0.7 : 1.15,
                              px: isDensePreview ? 1.25 : 1.75,
                              fontSize: isDensePreview ? '0.79rem' : '0.84rem',
                              borderBottomColor: (theme) => alpha(theme.palette.divider, 0.72),
                            },
                            '&:hover td': {
                              backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.035),
                            },
                          }}
                        >
                          <TableCell>{order.ID}</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>{order.OrderNumber}</TableCell>
                          <TableCell>{order.ExternalOrderReference}</TableCell>
                          <TableCell>{renderOrderTagChips(order.Tags)}</TableCell>
                          <TableCell>{order.TotalItems}</TableCell>
                          <TableCell>{order.CourierServiceName}</TableCell>
                        </TableRow>
                      ))}
                      {displayedOrders.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={6} align="center" sx={{ py: 5 }}>
                            <Stack spacing={0.5} alignItems="center">
                              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                No orders to display
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                Load a client queue to preview orders here before creating a batch.
                              </Typography>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                <Divider />

                <Box
                  sx={{
                    px: { xs: 1.25, md: 1.75 },
                    py: { xs: 1.15, md: 1.25 },
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 1,
                    flexWrap: 'wrap',
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Only the previewed orders shown above will be used when creating a new batch.
                  </Typography>
                  <Button
                    variant="contained"
                    color="success"
                    onClick={createBatch}
                    disabled={loading || orders.length === 0}
                    startIcon={loading ? null : <PlaylistAddCheckCircleRoundedIcon />}
                    sx={{ textTransform: 'none', fontWeight: 700, minWidth: 170 }}
                  >
                    {loading ? <CircularProgress size={22} /> : 'Create Batch'}
                  </Button>
                </Box>
              </Paper>
            </Stack>
          ) : (
            <Stack spacing={2} sx={{ minHeight: 0, flex: 1 }}>
              <Paper variant="outlined" sx={PANEL_SX}>
                <Stack spacing={1.75}>
                  <Box>
                    <Typography variant="overline" sx={{ color: 'primary.main', fontWeight: 700, letterSpacing: '0.08em' }}>
                      Scan Workflow
                    </Typography>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      Build a batch directly from a pasted or scanned order list.
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Choose the client and pick strategy first, then paste one order number per line to create a batch in one step.
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', md: '1.2fr 1fr auto' },
                      gap: 1.5,
                      alignItems: 'start',
                    }}
                  >
                    <Box>
                      <Typography sx={FIELD_LABEL_SX}>Select Client</Typography>
                      <Select
                        value={clientId}
                        onChange={(e) => setClientId(e.target.value)}
                        displayEmpty
                        fullWidth
                        size="small"
                      >
                        <MenuItem value="" disabled>Choose a client</MenuItem>
                        {clients.map((client) => (
                          <MenuItem key={client.id} value={client.id}>
                            {client.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </Box>

                    <Box>
                      <Typography sx={FIELD_LABEL_SX}>Pick Type</Typography>
                      <Select
                        value={pickType}
                        onChange={(e) => setPickType(e.target.value)}
                        fullWidth
                        size="small"
                      >
                        <MenuItem value="Standard">Standard</MenuItem>
                        <MenuItem value="Bulk">Bulk</MenuItem>
                        <MenuItem value="MultiTote">MultiTote</MenuItem>
                      </Select>
                    </Box>

                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: { xs: 'flex-start', md: 'flex-end' },
                        height: '100%',
                      }}
                    >
                      <FormControlLabel
                        control={(
                          <Checkbox
                            checked={printDispatch}
                            onChange={(e) => setPrintDispatch(e.target.checked)}
                          />
                        )}
                        label="Print Dispatch Notes"
                      />
                    </Box>
                  </Box>
                </Stack>
              </Paper>

              <Paper
                variant="outlined"
                sx={{
                  flex: { xs: 'unset', md: 1 },
                  minHeight: { xs: 'auto', md: 0 },
                  borderRadius: 3,
                  borderColor: 'divider',
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: { xs: 'visible', md: 'hidden' },
                }}
              >
                <Stack
                  spacing={1.5}
                  sx={{
                    p: { xs: 1.5, md: 1.75 },
                    flex: { xs: 'unset', md: 1 },
                    minHeight: { xs: 'auto', md: 0 },
                  }}
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={1}
                    justifyContent="space-between"
                    alignItems={{ md: 'center' }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ ...FIELD_LABEL_SX, mb: 0.35 }}>Paste or Scan</Typography>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        Paste or scan order numbers
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Add one order number per line. This is useful for quick, operator-led batch creation without loading the whole queue first.
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                      <Chip size="small" color="primary" variant="outlined" label={`${parsedOrderNumbers.length} detected`} />
                      <Chip size="small" variant="outlined" label={pickType} />
                    </Stack>
                  </Stack>

                  <TextField
                    multiline
                    minRows={8}
                    placeholder={`0PTO5WFX70NO\n0ABC123XYZ`}
                    variant="outlined"
                    fullWidth
                    value={orderListText}
                    onChange={(e) => setOrderListText(e.target.value)}
                    disabled={loading}
                    sx={{
                      flex: { xs: 'unset', md: 1 },
                      '& .MuiOutlinedInput-root': {
                        alignItems: 'flex-start',
                        backgroundColor: (theme) => alpha(theme.palette.background.paper, 0.92),
                      },
                      '& .MuiInputBase-inputMultiline': {
                        minHeight: { xs: '100px', md: 'auto' },
                      },
                    }}
                  />

                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: { xs: 'stretch', md: 'center' },
                      gap: 1,
                      flexWrap: 'wrap',
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      {parsedOrderNumbers.length
                        ? `${parsedOrderNumbers.length} order number${parsedOrderNumbers.length === 1 ? '' : 's'} detected and ready for batch creation.`
                        : 'Paste or scan order numbers to create a batch from a list.'}
                    </Typography>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={createBatchFromList}
                      disabled={loading || !clientId || orderListText.trim() === ''}
                      startIcon={loading ? null : <QrCodeScannerRoundedIcon />}
                      sx={{ textTransform: 'none', fontWeight: 700, minWidth: 210 }}
                    >
                      {loading ? <CircularProgress size={20} /> : 'Create Batch from List'}
                    </Button>
                  </Box>
                </Stack>
              </Paper>
            </Stack>
          )}

          <Dialog
            open={manageTagsOpen}
            onClose={closeManageTagsDialog}
            fullWidth
            maxWidth="sm"
          >
            <DialogTitle>Manage Tags</DialogTitle>
            <DialogContent dividers>
              <Stack spacing={1.5}>
                <Typography variant="body2" color="text.secondary">
                  Add or maintain the shared tag list used by the Include and Exclude filters.
                </Typography>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                  <TextField
                    label="New tag"
                    fullWidth
                    size="small"
                    value={newTagName}
                    onChange={(event) => setNewTagName(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        handleAddTag();
                      }
                    }}
                    disabled={tagActionLoading}
                  />
                  <Button
                    variant="contained"
                    onClick={handleAddTag}
                    disabled={tagActionLoading || !newTagName.trim()}
                    sx={{ textTransform: 'none', fontWeight: 700, minWidth: 124 }}
                  >
                    {tagActionLoading && !editingTagId && !tagToDelete ? <CircularProgress size={20} /> : 'Add Tag'}
                  </Button>
                </Stack>

                <Divider />

                <Stack spacing={1}>
                  {managedTags.length ? managedTags.map((tag) => (
                    <Paper
                      key={tag.id}
                      variant="outlined"
                      sx={{
                        p: 1.1,
                        borderRadius: 2,
                        borderColor: (theme) => alpha(theme.palette.primary.main, 0.12),
                      }}
                    >
                      {String(editingTagId) === String(tag.id) ? (
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                          <TextField
                            fullWidth
                            size="small"
                            value={editingTagValue}
                            onChange={(event) => setEditingTagValue(event.target.value)}
                            onKeyDown={(event) => {
                              if (event.key === 'Enter') {
                                event.preventDefault();
                                handleSaveTagEdit();
                              }
                            }}
                            disabled={tagActionLoading}
                          />
                          <Stack direction="row" spacing={1}>
                            <Button
                              variant="contained"
                              onClick={handleSaveTagEdit}
                              disabled={tagActionLoading || !editingTagValue.trim()}
                              sx={{ textTransform: 'none', fontWeight: 700 }}
                            >
                              {tagActionLoading ? <CircularProgress size={20} /> : 'Save'}
                            </Button>
                            <Button
                              variant="text"
                              onClick={cancelEditingTag}
                              disabled={tagActionLoading}
                              sx={{ textTransform: 'none', fontWeight: 700 }}
                            >
                              Cancel
                            </Button>
                          </Stack>
                        </Stack>
                      ) : (
                        <Stack
                          direction={{ xs: 'column', sm: 'row' }}
                          spacing={1}
                          justifyContent="space-between"
                          alignItems={{ sm: 'center' }}
                        >
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                              {tag.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Available in Include and Exclude filters
                            </Typography>
                          </Box>
                          <Stack direction="row" spacing={1}>
                            <Button
                              size="small"
                              variant="text"
                              onClick={() => beginEditingTag(tag)}
                              disabled={tagActionLoading}
                              sx={{ textTransform: 'none', fontWeight: 700 }}
                            >
                              Edit
                            </Button>
                            <Button
                              size="small"
                              color="error"
                              variant="text"
                              onClick={() => setTagToDelete(tag)}
                              disabled={tagActionLoading}
                              sx={{ textTransform: 'none', fontWeight: 700 }}
                            >
                              Delete
                            </Button>
                          </Stack>
                        </Stack>
                      )}
                    </Paper>
                  )) : (
                    <Alert severity="info" variant="outlined">
                      No tags are available yet. Add one above to start using it in the filters.
                    </Alert>
                  )}
                </Stack>
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={closeManageTagsDialog} disabled={tagActionLoading} sx={{ textTransform: 'none', fontWeight: 700 }}>
                Close
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog
            open={Boolean(tagToDelete)}
            onClose={() => {
              if (!tagActionLoading) setTagToDelete(null);
            }}
            fullWidth
            maxWidth="xs"
          >
            <DialogTitle>Delete Tag?</DialogTitle>
            <DialogContent>
              <Typography variant="body2" color="text.secondary">
                {tagToDelete
                  ? `Remove "${tagToDelete.name}" from the managed tag list? It will also be removed from any current Include or Exclude selections.`
                  : ''}
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => setTagToDelete(null)}
                disabled={tagActionLoading}
                sx={{ textTransform: 'none', fontWeight: 700 }}
              >
                Cancel
              </Button>
              <Button
                color="error"
                variant="contained"
                onClick={handleDeleteTag}
                disabled={tagActionLoading}
                sx={{ textTransform: 'none', fontWeight: 700 }}
              >
                {tagActionLoading && tagToDelete ? <CircularProgress size={20} /> : 'Delete'}
              </Button>
            </DialogActions>
          </Dialog>

          <Snackbar
            open={snackbar.open}
            autoHideDuration={6000}
            onClose={handleSnackbarClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            sx={{
              bottom: { xs: 16, sm: 24 },
            }}
          >
            <Alert
              onClose={handleSnackbarClose}
              severity={snackbar.severity}
              variant="filled"
              sx={{
                width: '100%',
                minWidth: { xs: 280, sm: 360 },
                borderRadius: 2,
                boxShadow: '0 14px 32px rgba(15, 23, 42, 0.24)',
                alignItems: 'flex-start',
                '& .MuiAlert-message': {
                  fontWeight: 600,
                  lineHeight: 1.35,
                },
              }}
            >
              {snackbar.message}
            </Alert>
          </Snackbar>
        </Stack>
      </Paper>
    </Layout>
  );

};

export default ManageBatches;
